import { Box, Divider, Grid, Heading, Icon, Text } from '@chakra-ui/react';
import {
  garpApi,
  GarpBranch,
  GarpCompareData,
} from '@texas/api/endpoints/erp/garpApi';
import { Icons } from '@texas/components/shared/Icons';
import { GarpFormContext } from '../SetGarpDataModal';
import { MiddleDivider } from '../shared/MiddleDivider';
import { useGarpStandardForm } from './useGarpStandardForm';
import {
  GarpColour,
  GarpExtraDescription,
  GarpMaterial,
  GarpPhoto,
  GarpSupplierColour,
  GarpSupplierItemCode,
  GarpTechnique,
  GarpTextMaterial,
  GarpTextSize,
  GarpSupplierTextMaterial,
  GarpWeight,
  GarpSupplierTextSize,
  OtherSupplierInfoTextFields,
  GarpCertificateMaterialText,
  OtherCustomerInfoTextField,
  PaDocumentNoTextField,
} from './StandardGarpRows';
import { RowDivider } from '../shared/RowDivider';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { useEffect } from 'react';
import { SpinnerWithLabel } from '@texas/components/shared/SpinnerWithLabel';
import { useTranslation } from 'react-i18next';

export function GarpStandardCompareView({
  data,
  variantId,
  branchId,
}: {
  data: GarpCompareData;
  variantId: number;
  branchId: number;
}) {
  const { t } = useTranslation();
  const {
    data: variant,
    refetch: refetchVariant,
    loading: variantLoading,
  } = useApiResource(garpApi.garpBranchPaCompareData);

  useEffect(() => {
    refetchVariant(variantId, branchId);
  }, [
    branchId,
    variantId,
    data.branchId,
    data.mainVariantId,
    data.variantId,
    refetchVariant,
  ]);

  return (
    <>
      <Box
        position="sticky"
        top={0}
        zIndex={1}
        py={2}
        bg="texas.bg.900"
        _light={{ bg: 'white' }}
      >
        <Grid gridTemplateColumns="40% 20% 40%" alignItems="center">
          <Box>
            <Heading fontSize="lg">{data.articleNumber}</Heading>
            <Text fontSize="sm" variant="sub">
              {data.versionName}
              {data.productApproval?.paNumber && (
                <> / {data.productApproval.paNumber}</>
              )}
            </Text>
          </Box>
          <Box display="flex" justifyContent="center">
            <Icon as={Icons.ArrowRight} />
          </Box>
          <Box>
            <Heading fontSize="lg">
              {variant?.variantName} / {variant?.branchName}
            </Heading>
          </Box>
        </Grid>
        <Divider mt={2} />
      </Box>

      {variantLoading && <SpinnerWithLabel label={t('general.loadingData')} />}
      {variant && <Form key={variant.variantId} garp={variant} data={data} />}
    </>
  );
}

function Form({ garp, data }: { garp: GarpBranch; data: GarpCompareData }) {
  const garpForm = useGarpStandardForm({
    variantId: garp.variantId,
    branchId: garp.branchId,
    defaultValues: {
      materialId: garp.materialId,
      techniqueId: garp.techniqueId,
      weight: garp.weight,
      categoryCodeId: garp.categoryCodeId,
      customsCodeId: garp.customsCodeId,
      countryOfOriginId: garp.countryOfOriginId,
      texts: garp.texts,
    },
  });

  return (
    <GarpFormContext.Provider value={garpForm}>
      <form {...garpForm.form}>
        <Grid
          gridTemplateColumns="[pa] 40% [mid] 20% [garp] 40% [end]"
          alignItems="center"
          gridRowGap={4}
          py={2}
        >
          <MiddleDivider rows={32} />
          <GarpPhoto
            variantId={garp.variantId}
            folderId={data.folderId}
            paFileIdentifier={data.productApproval?.fileIdentifier}
            paFileId={data.productApproval?.fileId}
          />
          <RowDivider rowIndex={2} />
          <GarpMaterial rowIndex={3} compositions={data.compositions} />
          <RowDivider rowIndex={4} />
          <GarpTechnique rowIndex={5} compositions={data.compositions} />
          <RowDivider rowIndex={6} />
          <GarpWeight rowIndex={7} compositions={data.compositions} />
          <RowDivider rowIndex={8} />
          <GarpExtraDescription />
          <RowDivider rowIndex={10} />
          <GarpColour compositions={data.compositions} />
          <RowDivider rowIndex={12} />
          <GarpTextMaterial rowIndex={13} />
          <RowDivider rowIndex={14} />
          <GarpTextSize rowIndex={15} compositions={data.compositions} />
          <RowDivider rowIndex={16} />
          <OtherCustomerInfoTextField index={4} />
          <RowDivider rowIndex={18} />
          <GarpCertificateMaterialText rowIndex={19} />
          <RowDivider rowIndex={20} />
          <PaDocumentNoTextField
            rowIndex={21}
            paNumber={data.productApproval?.paNumber}
          />
          <RowDivider rowIndex={22} variant="bold" label="Supplier" />
          <GarpSupplierItemCode
            rowIndex={23}
            supplierItemCode={data.productApproval?.supplierItemCode}
          />
          <RowDivider rowIndex={24} />
          <GarpSupplierColour compositions={data.compositions} />
          <RowDivider rowIndex={26} />
          <GarpSupplierTextMaterial compositions={data.compositions} />
          <RowDivider rowIndex={28} />
          <GarpSupplierTextSize
            compositions={data.compositions}
            rowIndex={29}
          />
          <RowDivider rowIndex={30} />
          <OtherSupplierInfoTextFields />
        </Grid>
      </form>
    </GarpFormContext.Provider>
  );
}
